// src/lib/proprio-creation.js — CRÉER ET RETIRER UN PROPRIÉTAIRE DEPUIS
// L'ÉCRAN LOGEMENTS, sans jamais laisser une fiche vide derrière soi.
//
// ══ L'INCIDENT DU 30/09 ═════════════════════════════════════════════════════
//
// Le bouton « ＋ Propriétaire » poussait AUSSITÔT une entrée aux champs vides
// dans le brouillon de configuration, sans rien ouvrir. La liste du parc
// n'affiche que des LOGEMENTS : un propriétaire sans logement n'y apparaît
// nulle part, donc le clic semblait ne rien faire. Dimitri a recliqué — quatre
// entrées vides (p9…p12), parties en base au premier « Enregistrer » suivant,
// visibles dans Argent › Facturation comme « 0 log. » sans nom, et que rien ne
// permettait de retirer (la seule feuille qui sait supprimer s'ouvre depuis la
// fiche d'un logement — qu'un propriétaire vide n'a pas).
//
// Ce module est PUR : il construit l'entrée à partir d'une saisie validée, et
// dit si une fiche peut être retirée. Aucune écriture ici — l'écriture passe
// par le chemin existant (saveCfg → /api/cfg, jeton de version).

import { propDisplayName } from './format'

const LONGUEUR_MAX_NOM = 120

function propre(v) {
  return String(v == null ? '' : v).trim()
}

/**
 * Construit l'entrée KV d'un propriétaire à partir de la saisie du formulaire.
 * LE NOM EST LE SEUL CHAMP OBLIGATOIRE : nom de famille pour un particulier,
 * raison sociale pour une société. Sans lui, RIEN n'est construit.
 *
 * Les clés sont celles de l'ancien `addProp` (vides comprises) : des écrans
 * lisent `p.adresse` sans garde, une clé manquante les ferait planter.
 *
 * @param {string} id        identifiant tiré par l'appelant (`p<n>`)
 * @param {object} saisie    { type: 'particulier'|'societe', nom_famille, prenom, nom, email, telephone, adresse, code_postal, ville }
 * @returns {{ok:true, entree:object} | {ok:false, raison:string}}
 */
export function construireProprio(id, saisie) {
  if (!id) return { ok: false, raison: 'Identifiant manquant.' }
  const s = saisie && typeof saisie === 'object' ? saisie : {}
  const societe = s.type === 'societe'
  const nom_famille = societe ? '' : propre(s.nom_famille)
  const prenom = societe ? '' : propre(s.prenom)
  const raisonSociale = societe ? propre(s.nom) : ''

  if (societe && !raisonSociale) return { ok: false, raison: 'Le nom de la société est obligatoire.' }
  if (!societe && !nom_famille) return { ok: false, raison: 'Le nom de famille est obligatoire.' }
  if ((nom_famille + prenom + raisonSociale).length > LONGUEUR_MAX_NOM * 2) {
    return { ok: false, raison: 'Le nom est trop long.' }
  }
  const email = propre(s.email)
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, raison: "L'e-mail n'a pas la forme d'une adresse (nom@domaine.fr)." }
  }

  const entree = {
    id,
    nom_famille,
    prenom,
    email,
    telephone: propre(s.telephone),
    adresse: propre(s.adresse),
    code_postal: propre(s.code_postal),
    ville: propre(s.ville),
    nom: raisonSociale,
    logs: [],
  }
  // Le nom affiché est dérivé au même endroit que le bouton Enregistrer
  // (NOM Prénom pour un particulier, raison sociale sinon).
  entree.nom = propDisplayName(entree) || raisonSociale
  return { ok: true, entree }
}

/** Une fiche SANS NOM ET SANS LOGEMENT : exactement ce que l'ancien bouton
 * fabriquait. Elle ne porte rien — ni argent, ni historique — et ne doit
 * jamais faire une ligne dans Facturation. */
export function estProprioVide(prop) {
  if (!prop) return true
  const nom = propre(propDisplayName(prop) || prop.nom)
  const nbLogs = Array.isArray(prop.logs) ? prop.logs.length : 0
  return !nom && nbLogs === 0
}

/**
 * Ce qui empêche de retirer un propriétaire. Liste VIDE = retirable.
 *
 * Ce que l'écran peut savoir, côté navigateur :
 *   · ses logements (le lien fort : factures, réservations, données de mois
 *     et incidents sont tous clés par l'identifiant du LOGEMENT) ;
 *   · les incidents rattachés à son identifiant (`propId`) ;
 *   · les factures émises à son NOM (`meta.prop_nom`, figé à l'émission —
 *     une facture ne porte PAS l'identifiant du propriétaire). Ce rapprochement
 *     par nom sert UNIQUEMENT à REFUSER : un faux positif garde une fiche, il
 *     ne perd jamais rien. Un nom vide ne rapproche rien ;
 *   · une série de factures (`invoice_prefix`) déjà posée ;
 *   · p6, la sous-location de la conciergerie, jamais retirable d'ici.
 *
 * @returns {string[]} raisons, en français simple
 */
export function raisonsNonSupprimable(prop, { invoicesList = [], incidents = [] } = {}) {
  if (!prop) return ['Propriétaire introuvable.']
  const raisons = []
  if (prop.id === 'p6') raisons.push('c\'est la sous-location de la conciergerie')
  const nbLogs = Array.isArray(prop.logs) ? prop.logs.length : 0
  if (nbLogs > 0) raisons.push(`il a ${nbLogs} logement${nbLogs > 1 ? 's' : ''}`)
  const noms = new Set(
    [propDisplayName(prop), prop.nom].map((n) => propre(n).toLowerCase()).filter(Boolean),
  )
  const nbFactures = noms.size
    ? (invoicesList || []).filter((f) => noms.has(propre(f && f.prop).toLowerCase())).length
    : 0
  if (nbFactures > 0) raisons.push(`${nbFactures} document${nbFactures > 1 ? 's' : ''} déjà émis à son nom`)
  const nbIncidents = (incidents || []).filter((i) => i && i.propId === prop.id).length
  if (nbIncidents > 0) raisons.push(`${nbIncidents} incident${nbIncidents > 1 ? 's' : ''} rattaché${nbIncidents > 1 ? 's' : ''}`)
  if (propre(prop.invoice_prefix)) raisons.push('une série de factures lui est attribuée')
  return raisons
}
